import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
} from "@mui/material";
import { 
    useState 
} from "react";
import { 
    OutlinedSelect 
} from "@components/outlined-select";
import { 
    DataTableLabels, 
    TableCellInputType 
} from "@components/data-table/types";
import {
  DataTableCellFormInput,
  DataTableCellInput,
} from "@components/data-table/data-table-input-value";
import { 
    useFormState 
} from "@hooks/use-form-state";
import {
  getFieldInputType,
  getOptionsFromSelectFieldConfig,
} from "@hooks/use-config/utils";
import { 
    AssetAutocomplete 
} from "../add-ticket-modal/asset-autocomplete/index";
import { 
    TICKET_KEY_FOR_ASSETS 
} from "@hooks/use-table-context/ticket/utils";

export const TableEditItemsPopover = ({
  anchor,
  onClose,
  keyLabels,
  keyOptions,
  itemLabel,
  editItems,
  config,
  tableColumns = [],
  formatForSave,
  itemIds = [],
}: {
  anchor: HTMLElement | null;
  onClose: () => void;
  keyLabels: DataTableLabels;
  keyOptions: string[] | null;
  itemLabel: string;
  editItems: (args: { items: (string | number)[]; formattedUpdate: any }) => void;
  config: Record<string, any>;
  tableColumns: any[];
  formatForSave: (toSave: Record<string, any>, config: Record<string, any>) => any;
  itemIds?: (string | number)[];
}) => {
  const [editKey, setEditKey] = useState<string>("");
  const [cellValue, setCellValue] = useState<string | null>(null);

  const currentColumn = tableColumns?.find((col) => col.id === editKey);
  const currentConfig = config?.[editKey];
  const isForm = currentColumn?.inputType === TableCellInputType.Form;

  const initialState = isForm
    ? currentConfig.fields?.reduce((acc: Record<string, string>, field: any) => {
        acc[field.id] = "";
        return acc;
      }, {} as Record<string, string>)
    : {};
  const { state, handleChange, handleReset } = useFormState({ initialState });

  const submitEdit = () => {
    setEditKey("");
    setCellValue("");
    handleReset();

    const update = {
      [editKey]: editKey === TICKET_KEY_FOR_ASSETS ? cellValue : (isForm ? state : cellValue),
    };
    const formattedUpdate = formatForSave(update, config) ? formatForSave(update, config) : update;

    editItems({
      items: itemIds,
      formattedUpdate,
    });
    onClose();
  };

  const isValidInput = editKey === TICKET_KEY_FOR_ASSETS
    ? cellValue !== null
    : isForm
    ? Object.values(state ?? {}).some((value) => value !== null && value !== "")
    : cellValue !== null;

  return (
    <Dialog
      className="table-controls-popup"
      open={!!anchor}
      onClose={onClose}
      sx={{
        "& .MuiPaper-root": {
          minWidth: {
            sx: 300,
            md: 450,
          },
        },
      }}
    >
      <DialogTitle>Edit ({itemIds.length}) {itemLabel}</DialogTitle>
      <Divider />
      <DialogContent>
        <Stack gap={2}>
          <OutlinedSelect
            label="Select a Column"
            inputRef={(inputRef) => editKey === "" && inputRef?.focus()}
            value={editKey}
            onChange={setEditKey}
            options={keyOptions
              ?.filter(key => tableColumns.find(col => col.id === key)?.inputType !== TableCellInputType.SystemProvided)
              ?.map((key) => ({
              value: key,
              label: keyLabels[key],
            }))}
            sx={{ minWidth: 180 }}
          />
          {!isForm && editKey !== TICKET_KEY_FOR_ASSETS && (
            <DataTableCellInput
              currentInputType={currentColumn?.inputType as TableCellInputType}
              value={cellValue}
              setValue={setCellValue}
              minWidth={"100%"}
              maxWidth={"100%"}
              label="New Value"
              options={getOptionsFromSelectFieldConfig(currentConfig) as any}
            />
          )}
          {editKey === TICKET_KEY_FOR_ASSETS && (
            <AssetAutocomplete 
              value={cellValue}
              onChange={(value) => setCellValue(value?.[0]?.toString() || null)}
              label={"New Value"}
            />
          )}
          {isForm && (
            <DataTableCellFormInput
              id={currentColumn?.id}
              inputProps={
                (currentConfig?.fields?.map((field: any) => ({
                  currentInputType: getFieldInputType(field),
                  value: state?.[field.id] ?? "",
                  setValue: handleChange(field.id),
                  minWidth: "100%",
                  maxWidth: "100%",
                  label: field.name,
                  options: getOptionsFromSelectFieldConfig(
                    config[field.id]
                  ),
                })) ?? []) as any
              }
            />
          )}

        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          variant="contained"
          disabled={!isValidInput}
          onClick={submitEdit}
        >
          Edit {itemLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
